import {
  FC,
  PropsWithChildren,
  useMemo,
  useRef,
  useState,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import { Form } from 'antd';

import { HeaderTabFire } from '../../components/TabsPropertyDataFire/components';
import { TabsPropertyDataFire } from '../../components';

import {
  GenericDataFormContext,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../../shared/contexts';
import { StepThreeFormContext } from './StepThreeFormContext';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import {
  DECISION_TYPE,
  Feature,
  FeaturesVariousRisksRequest,
  RequestPlansByProducer,
  SelectOption,
} from '../../../../../../../../../shared/interfaces';
import { QuotationStep } from '../../../../../shared';
import { LOADING_SAVED_QUOTATIONS_STATES } from '../../../../../shared/interfaces/loading-saved-quotations-states-enum';
import { displayErrorMessage } from '../../../../../../../../../shared/utils';
import { LoadQuotationToRenewResponse } from '../../../../../shared/interfaces/load-renewal-quotation-response-interface';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';

export const StepThreeFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const INITIAL_KEY_TAB = 1;

  const { rubricList } = useContext(WizardDataFormContext);
  const { clientSelected } = useContext(StepOneFormContext);
  const {
    currentStep,
    quotationInfo,
    loadingSavedQuotation,
    loadSaveQuotationState,
    setLoadSaveQuotationState,
    savedQuotation,
    quotationToRenewal,
    loadingRenewalQuotation,
    setRenewalQuotationState,
    renewalQuotationState,
  } = useContext(QuotationInfoContext);

  const { 
    setCanShowSpinner, 
    getLocalitiesPerProvince, 
    updateFormsAfterRemoveTabs,
    removedTabIndexes,
    setRemovedTabIndexes, 
    setUserCanDeleteTabs
  } = useContext(GenericDataFormContext);
  const { rubricSelected } = useContext(StepTwoFormContext);

  const [activeKey, setActiveKey] = useState<number>(INITIAL_KEY_TAB);
  const [allFeatures, setAllFeatures] =
    useState<
      Array<{ plan: string; keyTab: number; features: Array<Feature> }>
    >([]);
  const [fireFormValues, setFireFormValues] = useState<any>();
  const [hasStepThreeFormChanged, setStepThreeHasFormChanged] =
    useState<boolean>(true);
  const [keyTabToDelete, setKeyTabToDelete] = useState<number | null>(null);
  const [plansByActivityList, setPlansByActivityList] = useState<
    Array<SelectOption>
  >([]);
  const [showModal, setShowModal] = useState<boolean>(false);

  const [tabs, setTabs] = useState<Array<any>>([
    {
      label: <HeaderTabFire id={INITIAL_KEY_TAB} />,
      children: <TabsPropertyDataFire id={INITIAL_KEY_TAB} />,
      key: INITIAL_KEY_TAB,
      closable: false,
      forceRender: true,
    },
  ]);

  const [stepThreeForm] = Form.useForm<any>();

  const newTabIndex = useRef(INITIAL_KEY_TAB);

  const addNewTab = useCallback(() => {
    if (tabs.length <= 5) {
      const newActiveKey = ++newTabIndex.current;

      setTabs([
        ...tabs,
        {
          label: <HeaderTabFire id={newActiveKey} />,
          children: <TabsPropertyDataFire id={newActiveKey} />,
          key: newActiveKey,
          closable: false,
          forceRender: true,
        },
      ]);

      setActiveKey(newActiveKey);

      if(removedTabIndexes.includes(newActiveKey)) {
        setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
      };

      return;
    }

    displayErrorMessage('Solo puede agregar hasta 6 Incendios');
  }, [
    tabs, 
    newTabIndex, 
    setTabs, 
    setActiveKey,
    newTabIndex,
    removedTabIndexes,
    setRemovedTabIndexes,
  ]);

  const duplicateTab = useCallback(
    (id: any) => {
      if (tabs.length <= 5) {
        const tabSelected = tabs.find((tab: any) => tab.key === id);
        const newActiveKey = ++newTabIndex.current;

        if (tabSelected) {
          const newTab = {
            ...tabSelected,
            key: newActiveKey,
            label: <HeaderTabFire id={newActiveKey} />,
            children: (
              <TabsPropertyDataFire id={newActiveKey} duplicateId={id} />
            ),
            closable: false,
            forceRender: true,
          };

          setTabs([...tabs, newTab]);
          setActiveKey(newActiveKey);
          if(removedTabIndexes.includes(newActiveKey)) {
            setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
          };
        }

        return;
      }

      displayErrorMessage('Solo puede agregar hasta 6 Incendios');
    },
    [
      tabs, 
      newTabIndex, 
      setTabs, 
      setActiveKey,
      newTabIndex,
      setRemovedTabIndexes,
      removedTabIndexes
    ]
  );

  const removeTab = useCallback(
    (targetKey: number) => {
      setCanShowSpinner(true);

      setTimeout(() => {
        updateFormsAfterRemoveTabs([targetKey], [stepThreeForm]);

        setRemovedTabIndexes((prevValues) => [ ...prevValues, targetKey ]);

        const targetIndex = tabs.findIndex(
          (pane: any) => pane.key === targetKey
        );

        const newPanes = tabs.filter((pane: any) => pane.key !== targetKey).map((el, i) => ({
          ...el,
          key: i + 1,
          label: <HeaderTabFire id={i + 1} />,
          children: <TabsPropertyDataFire id={i + 1} />,
        }));

        if (newPanes.length && targetKey === activeKey) {
          const { key } =
            newPanes[
            targetIndex === newPanes.length ? targetIndex - 1 : targetIndex
            ];

          setActiveKey(key);
        }

        newTabIndex.current = tabs.length - 1; 

        setTabs(newPanes);
        setCanShowSpinner(false);
      }, 500);
    },
    [
      tabs, 
      setTabs, 
      setActiveKey, 
      activeKey,
      updateFormsAfterRemoveTabs,
      stepThreeForm,
      setRemovedTabIndexes
    ]
  );

  const getListPlansActivity = useCallback(async () => {

    setCanShowSpinner(true);

    const params: RequestPlansByProducer = {
      capituloTarifaInciso1: rubricSelected?.capituloTarifaInciso1!,
      arse: quotationInfo?.arse!,
      articulo: quotationInfo?.article!,
      capituloTarifaInciso2: rubricSelected?.capituloTarifaInciso2!,
      capituloTarifa: rubricSelected?.capituloTarifa!,
      rama: quotationInfo?.branch!,
    };

    try {
      const response = await QuotationService.getPlansByProducerAndActivity(
        params
      );

      if (response?.data.planes.length) {
        const options = response.data.planes.map((el) => ({
          label: el.descripcionLarga,
          value: el.codigo,
        }));

        setPlansByActivityList(options);
      }
    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [rubricList, rubricSelected, quotationInfo]);

  const getFeaturesRisks = useCallback(
    async (
      planSelected: string,
      componente: number
    ): Promise<Array<Feature> | any> => {
      const params: FeaturesVariousRisksRequest = {
        numeroCotizacion: quotationInfo?.token!,
        rama: quotationInfo?.branch!,
        articuloRamaSecuencia: quotationInfo?.arse!,
        plan: planSelected,
        codigoPostal: clientSelected?.codigoPostal!,
        sufijoPostal: clientSelected?.sufijoCodigoPostal!,
        componente,
        ubicacionDeRiesgo: '',
      };

      try {
        const response = await QuotationService.postFeaturesVariousRisks(
          params
        );

        return response?.data?.caracteristicas ?? [];
      } catch (error) { }
    },
    [quotationInfo, clientSelected]
  );


  const getFeaturesRiskByPlan = useCallback(
    async (id: number, planSelected: string, componente: number) => {
      const isFeatureAlreadyAdded = allFeatures.some(
        (feature) => feature.plan === planSelected
      );

      if (isFeatureAlreadyAdded) return;

      setCanShowSpinner(true);

      try {
        const featuresList = await getFeaturesRisks(planSelected, componente);

        setAllFeatures((prevValues) => [
          ...prevValues,
          { plan: planSelected, keyTab: id, features: featuresList },
        ]);
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      setCanShowSpinner, 
      setAllFeatures, 
      getFeaturesRisks
    ]
  );


  const fillStepThreeServices = useCallback(async (isRenewal: boolean) => {
    try {
      let quotationToProccess: LoadQuotationToRenewResponse;
      if(isRenewal){
        quotationToProccess = quotationToRenewal;
      }else{
        quotationToProccess = savedQuotation;
      }
      const promises: Array<Promise<void>> = [
        getListPlansActivity(),
      ];

      const provincesSelected: Array<string> = [];
      const plansSelected: Array<{
        componentId: string;
        plan: string;
      }> = [];

      quotationToProccess.bienes.forEach((bien: any) => {
        if(!provincesSelected.includes(bien.provincia)) {
          provincesSelected.push(bien.provincia);
        };

        if(!plansSelected.some( el => el.componentId === bien.numeroComponente && el.plan === bien.planSeleccionado)) {
          plansSelected.push({
            componentId: String(bien.numeroComponente),
            plan: String(bien.planSeleccionado),
          });
        };
      });

      provincesSelected.forEach((province) => promises.push(getLocalitiesPerProvince(province)));
      plansSelected.forEach(({ componentId, plan }) =>
        promises.push(
          getFeaturesRiskByPlan(
            Number(componentId),
            plan,
            Number(componentId)
          )
        )
      );

      await Promise.all(promises).then(() =>{
        if(isRenewal){
          setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL);
        }else{
          setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED);
        }
      })
    } catch (error) {}
  }, [
    savedQuotation, 
    quotationToRenewal,
    getListPlansActivity,
    getFeaturesRiskByPlan,
    setLoadSaveQuotationState,
    getLocalitiesPerProvince,
  ]);

  const fillStepThreeData = useCallback(async (isRenewal: boolean) => {
    const _tabs: Array<any> = [];
    const _inputs: Array<{ name: string, value: string | boolean }> = [];
    setUserCanDeleteTabs(false);
    let quotationToProccess: LoadQuotationToRenewResponse;
    if(isRenewal){
      quotationToProccess = quotationToRenewal;
    }else{
      quotationToProccess = savedQuotation;
    }
    quotationToProccess.bienes.forEach((bien: any, index: number) => {
      const _activeKey = index + 1;
      _tabs.push({
        label: <HeaderTabFire id={_activeKey} />,
        children: <TabsPropertyDataFire id={_activeKey} />,
        key: _activeKey,
        closable: false,
        forceRender: true,
      });

      _inputs.push({ 
        name: `plan-tab-${_activeKey}`, 
        value: `${bien.planSeleccionado}` 
      });
      _inputs.push({ 
        name: `province-tab-${_activeKey}`, 
        value: `${bien.provincia}` 
      });
      _inputs.push({ 
        name: `location-tab-${_activeKey}`, 
        value: `${bien.sufijoPostal}${bien.codigoPostal}` 
      });
      _inputs.push({
        name: `propertyDataAddress${_activeKey}`,
        value: `${bien.domicilio}`,
      });

      allFeatures.find((feature) => feature.keyTab === _activeKey)?.features.forEach((_feature) => {
        const apply = bien.carecteristicasSeleccionadas.find((e: any) => e.codigo === _feature.codigo && e.tiene === DECISION_TYPE.YES) !== undefined;
        _inputs.push({ 
          name: `feature-tab-${_activeKey}-${_feature.codigo}`, 
          value: apply
        });
      });
      
    });

    newTabIndex.current = quotationToProccess.bienes.length;

    setTabs(_tabs);

    setActiveKey(INITIAL_KEY_TAB);
    
    stepThreeForm.setFields(_inputs);
    
    setFireFormValues(_inputs);
  }, [
    savedQuotation,
    quotationToRenewal,
    setActiveKey,
    setTabs,
    stepThreeForm,
    setFireFormValues,
    allFeatures,
    setUserCanDeleteTabs
  ]);

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && !loadingSavedQuotation && !plansByActivityList.length) {
      getListPlansActivity();
    }
  }, [currentStep, getListPlansActivity, loadingSavedQuotation, plansByActivityList]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADING);
      fillStepThreeServices(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING);
      fillStepThreeData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {    
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLING &&
      tabs.length === savedQuotation.bienes.length
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_THREE_DATA_FILLED);
    }
  }, [
    loadSaveQuotationState,
    savedQuotation,
    tabs,
  ]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (currentStep === QuotationStep.THREE && !loadingRenewalQuotation && !plansByActivityList.length) {
      getListPlansActivity();
    }
  }, [currentStep, getListPlansActivity, loadingRenewalQuotation, plansByActivityList]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADING_RENEWAL);
      fillStepThreeServices(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL);
      fillStepThreeData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLING_RENEWAL &&
      tabs.length === quotationToRenewal.bienes.length
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_THREE_DATA_FILLED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    quotationToRenewal,
    tabs,
  ]);

  const values = useMemo(
    () => ({
      stepThreeForm,
      hasStepThreeFormChanged,
      setStepThreeHasFormChanged,
      activeKey,
      setActiveKey,
      tabs,
      setTabs,
      addNewTab,
      duplicateTab,
      removeTab,
      newTabIndex,
      fireFormValues,
      setFireFormValues,
      plansByActivityList,
      getFeaturesRisks,
      getFeaturesRiskByPlan,
      allFeatures,
      showModal,
      setShowModal,
      keyTabToDelete,
      setKeyTabToDelete,
      setAllFeatures,
      getListPlansActivity,
    }),
    [
      stepThreeForm,
      hasStepThreeFormChanged,
      setStepThreeHasFormChanged,
      activeKey,
      setActiveKey,
      tabs,
      setTabs,
      addNewTab,
      duplicateTab,
      removeTab,
      newTabIndex,
      fireFormValues,
      setFireFormValues,
      plansByActivityList,
      getFeaturesRisks,
      getFeaturesRiskByPlan,
      allFeatures,
      showModal,
      setShowModal,
      keyTabToDelete,
      setKeyTabToDelete,
      setAllFeatures,
      getListPlansActivity,
    ]
  );

  return (
    <StepThreeFormContext.Provider value={values}>
      {children}
    </StepThreeFormContext.Provider>
  );
};
